/**
 * @render react
 * @name LoadMore
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withAmp } from 'Components/protons/withAmp';
import { withPolaris } from 'Components/protons/Polaris';

import { Button } from 'Components/atoms/Button';
import { Link } from 'Components/molecules/Link';

import renderAmp from './amp';
import './_style.scss';

/**
 * LoadMore component
 *
 * @param {string} label - label to show on the element text
 * @param {string} href - Href attribute
 * @param {Function} load - callback function to be invoked on component click
 * @param {Boolean} isLoading - This is a boolean that indicates whether the page is actively loading new data, so the button can update UX and disable click events appropriately
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - extra classes to be applied to the component
 * @param {Object} context - extra information coming from Polaris provider
 * @param {string} buttonStyle - cssPostfix to apply one of the predifined styles, possible values - "primary", "secondary", "on-background"
 */
class LoadMore extends PureComponent {
  /**
   * Renders the amp version of the component.
   */
  amp() {
    return renderAmp.bind(this)();
  }

  /**
   * Render function
   */
  render() {
    const {
      label,
      load,
      isLoading,
      loadStyles,
      extraClassNames,
      context: { cssPrefix },
      buttonStyle,
      href,
    } = this.props;

    return (
      <div
        role="presentation"
        className={classNames({
          [`${cssPrefix}__load-more`]: loadStyles,
          ...extraClassNames,
        })}
      >
        {href ? (
          <Link
            href={href}
            onClick={!isLoading ? (e) => {
              e.preventDefault();
              load();
            } : undefined}
            extraClassNames={{
              [`${cssPrefix}__load-more--link`]: true,
              '-is-loading': isLoading,
            }}
          >
            {isLoading ? 'Loading' : label}
          </Link>
        ) : (
          <Button
            type="button"
            label={isLoading ? 'Loading' : label}
            onClick={!isLoading ? load : undefined}
            variant={`-${buttonStyle}`}
            extraClassNames={{ '-is-loading': isLoading }}
          />
        )}
      </div>
    );
  }
}

/**
 * Prop Types
 */
LoadMore.propTypes = {
  label: PropTypes.string,
  load: PropTypes.func,
  isLoading: PropTypes.bool,
  context: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.instanceOf(Object),
  buttonStyle: PropTypes.string,
  href: PropTypes.string,
};

/**
 * Default Props
 */
LoadMore.defaultProps = {
  label: '',
  load: () => {},
  isLoading: false,
  context: {},
  loadStyles: true,
  extraClassNames: {},
  buttonStyle: 'primary',
  href: null,
};

export default compose(withPolaris, withAmp)(LoadMore);
